<script lang="ts">
  import type {
    AgreementSpot,
    Debtor,
    DebtorAccess,
    DebtorData,
    NewQueueApplicant,
    Queue,
    QueueApplicantVolition,
    Tag,
  } from "../../../../apis/parkito";
  import { err, ok, type Result } from "$shared/lib/result";
  import { Col2Grid, Ver } from "$components";
  import { withApi } from "../../../lib/api-parkito";
  import { Form, Validate } from "../../../lib/validation_next";
  import DebtorAdd from "../../../../projects/dpo/components/debtors/DebtorAdd.svelte";
  import Separator from "../../Separator.svelte";
  import { Label, Toast } from "$components";
  import ValCheckbox from "../../ValCheckbox.svelte";
  import ValInputField from "../../ValInputField.svelte";
  import ValSelect from "../../ValSelect.svelte";
  import { onMount } from "svelte";
  import { get, writable } from "svelte/store";
  import CustomButton from "../../CustomButton.svelte";
  import DebtorSelector from "../../../../projects/dpo/components/debtors/DebtorSelector.svelte";
  import { pop } from "svelte-spa-router";
  import QueueApplicantVolitionSelector from "./QueueApplicantVolitionSelector.svelte";

  type DebtorType = { new: DebtorData } | { existing: string };
  type NewQueueApplicantWithDebtor = Omit<NewQueueApplicant, "debtorId"> & {
    debtor: DebtorType;
  };

  export const siteTags: Tag[] = [];
  // Value from subrouter Root.svelte
  export let queueId: string;
  export let submitted: boolean | undefined = undefined;
  export let selectableDebtors: Array<DebtorAccess> | undefined = undefined;

  let debtorForm = Form.input<DebtorData>();
  // If the debtor is selected from the DebtorSelector component
  let adminSelectedDebtor = writable<Debtor | undefined>(undefined);
  let queueData: Queue | undefined = undefined;

  // Debtor variant select
  const debtorVariantSelectItems = [
    {
      label: "Ny parkör",
      value: "new",
      disabled: false,
    },
    {
      label: "Existerande parkör",
      value: "existing",
      disabled: false,
    },
  ];
  const debtorVariantSelect = Form.multiSelect(
    debtorVariantSelectItems,
    debtorVariantSelectItems[
      selectableDebtors && selectableDebtors.length > 0 ? 1 : 0
    ],
  );

  const debtorSelectItems =
    selectableDebtors && selectableDebtors.length > 0
      ? selectableDebtors.map((debtor) => {
        return {
          label: `${debtor.debtor.name} - ${debtor.debtor.contactEmail}`,
          value: debtor.debtor.id,
          disabled: false,
        };
      })
      : [
        {
          label: "Inga tillgängliga parkörer",
          value: "null",
          disabled: true,
        },
      ];
  const userDebtorSelect = Form.multiSelect(
    debtorSelectItems,
    debtorSelectItems[0],
  );

  let agreementSpotsOfDebtor = writable<AgreementSpot[]>([]);
  let updateAgreementSpotsOfDebtor = async (
    debtorId: Result<string, string>,
    selection: Result<string, string>,
  ) => {
    if (
      debtorId.isOk() && selection.isOk() && selection.value === "existing"
    ) {
      const res = await withApi((api) => {
        // TODO admin only?
        return api.debtorGetAgreements(debtorId.value);
      });

      if (res.isOk()) {
        agreementSpotsOfDebtor.set(res.value.flatMap((agr) => {
          let approvedAt = Date.parse(agr.approvedAt ?? "");
          let endDate = Date.parse(agr.data.endDate ?? "");
          let startDate = Date.parse(agr.data.startDate ?? "");
          let isActive = approvedAt <= Date.now() &&
            (Date.now() < endDate || !endDate) &&
            startDate <= Date.now();

          return isActive ? agr.spots : [];
        }));
      }
    } else if (selection.isOk() && selection.value === "existing") {
      agreementSpotsOfDebtor.set([]);
    } else if (selection.isOk() && selection.value === "new") {
      agreementSpotsOfDebtor.set([]);
    } else {
      agreementSpotsOfDebtor.set([]);
    }
  };

  let adminSelectedDebtorForm = Form.map<Debtor | undefined, string>(
    Form.fromStore(adminSelectedDebtor),
    (debtor) => {
      if (debtor === undefined) {
        return { hasChanged: true, res: err("Måste välja en parkör") };
      } else {
        return { hasChanged: true, res: ok(debtor.id) };
      }
    },
  );
  let selectedDebtorId = Form.firstOk(
    adminSelectedDebtorForm,
    userDebtorSelect,
  );
  selectedDebtorId.output.subscribe(async (debtorId) => {
    await updateAgreementSpotsOfDebtor(
      debtorId.res,
      get(debtorVariantSelect).res,
    );
  });
  debtorVariantSelect.subscribe(async (variantSelect) => {
    await updateAgreementSpotsOfDebtor(
      get(selectedDebtorId.output).res,
      variantSelect.res,
    );
  });

  // -- Start date
  let fieldStartDate = Form.field(
    Validate.required("Startdatum måste anges"),
  );

  let fieldResidentalParking = Form.checkbox(Validate.boolean());
  let volition = Form.input<QueueApplicantVolition>(undefined, {
    first: { tags: [] },
  });

  let queueForm = Form.object<NewQueueApplicantWithDebtor>({
    debtor: Form.nest(Form.oneOf<DebtorType>(
      debtorVariantSelect,
      {
        new: Form.nest(Form.object({ new: debtorForm })),
        existing: Form.nest(
          Form.object({
            existing: selectedDebtorId,
          }),
        ),
      },
    )),
    preferredStartDate: fieldStartDate,
    volition,
    wantsResidentalVatFree: fieldResidentalParking,
  });

  const addQueueApplicant = async (applicant: NewQueueApplicant) => {
    const res = await withApi((api) => {
      return api.queuePostApplicant(queueId, applicant);
    });
    if (res.isOk()) {
      if (submitted === undefined) {
        Toast.info("Ny intresseanmälan skapad");
        pop();
      } else {
        submitted = true;
      }
    } else {
      Toast.error("Intresseanmälan kunde ej skapas");
    }
  };

  const addDebtor = async (debtorData: DebtorData): Promise<string> => {
    const res = await withApi((api) => {
      return api.debtorCreate(debtorData);
    });
    if (res.isOk()) {
      if (import.meta.env.DEV) console.log("Debtor added");
      return res.value.id;
    } else {
      throw new Error("Parkör kunde ej skapas");
    }
  };

  const submitForm = async () => {
    if ($queueForm.res.isOk() && $queueForm.hasChanged) {
      let formData = $queueForm.res.value;
      let debtorId;
      if ("new" in formData.debtor) {
        try {
          debtorId = await addDebtor(formData.debtor.new);
        } catch {
          debtorForm.setError("Parkör kunde ej skapas");
          Toast.error("Parkör kunde ej skapas");
        }
      } else {
        debtorId = formData.debtor.existing;
      }

      if (debtorId) {
        const createQueueApplicant = { ...formData, debtorId };
        addQueueApplicant(createQueueApplicant);
      } else {
        Toast.error("Systemfel - kunde inte få parkör-ID");
      }
    } else {
      Toast.error("Formuläret innhåller fel");
    }
  };

  const getQueueData = async (queueId: string) => {
    const res = await withApi((api) => {
      return api.queueGet(queueId);
    });
    if (res.isOk()) {
      queueData = res.value;
    } else {
      Toast.error("Ködata kunde ej hämtas");
    }
  };

  onMount(() => {
    getQueueData(queueId);
  });
</script>

<Ver gap="m">
  <h3 class="text-[15px]">Parkör (avtalstagare)</h3>
  <Col2Grid>
    <ValSelect
      name="sid"
      field={debtorVariantSelect}
    />
  </Col2Grid>
  {#if     $debtorVariantSelect.res.isOk() &&
      $debtorVariantSelect.res.value === "new"}
    <!-- Debtor form data comes with setContext -->
    <Separator />
    <DebtorAdd
      requestClose={() => {}}
      debtorData={null}
      submitForm={() => {}}
      buttonsInvisible={true}
      bind:debtorOutput={debtorForm}
    />
  {:else if     $debtorVariantSelect.res.isOk() &&
      $debtorVariantSelect.res.value === "existing"}
    {#if !selectableDebtors}
      <!-- Any debtor -->
      <Separator />
      <DebtorSelector
        label="Parkör (avtalstagare)"
        preSelectedDebtorId={undefined}
        bind:selectedDebtor={$adminSelectedDebtor}
      />
    {:else}
      <!-- One of the existing debtors connected to the current user -->
      <ValSelect
        name="selected-existing-debtor"
        label="Ange existerande parkör (avtalstagare)"
        field={userDebtorSelect}
      />
    {/if}
  {/if}

  <Separator />

  <!-- Start date -->
  <h3 class="text-[15px]">Önskat startdatum *</h3>
  <Col2Grid>
    <ValInputField
      name="startdate"
      label="Önskat startdatum för att börja använda tjänsten, fakturering sker enligt avtalets startdatum"
      field={fieldStartDate}
      type="date"
    />
  </Col2Grid>

  {#if queueData?.allowResidentalVatFree}
    <Separator />

    <!-- Residental -->
    <h3 class="text-[15px]">Boende</h3>
    <ValCheckbox
      name="residental-parking"
      field={fieldResidentalParking}
      label="Boende i fastigheten"
    />
  {/if}

  <Separator />

  <!-- Other info -->
  {#if queueData}
    <!--QueueApplicantVolitionSelector
      context={{
        siteTags,
        agreementSpotsOfDebtor,
        queue: queueData,
        showResidentalParkingPrices: $fieldResidentalParking.res.isOk() &&
          $fieldResidentalParking.res.value,
      }}
      formOutput={volition}
    /-->
  {/if}

  {#if !$queueForm.res.isOk()}
    <Label text={$queueForm.res.error} type="error" />
  {/if}
  <!-- Submit buttons -->
  <div class="flex justify-end">
    <CustomButton
      disabled={!$queueForm.res.isOk()}
      type="primary"
      onClick={submitForm}
    >Lägg till</CustomButton>
  </div>
</Ver>
